// 1章 各章共通事項 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_PRIORITY = `
<svg viewBox="0 0 640 280" role="img" aria-label="設計図書の優先順位。1 質問回答書、2 現場説明書、3 特記仕様書、4 別冊の図面、5 標準仕様書の順に優先される">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="150" y="16"  width="360" height="40" rx="8" opacity="0.9"/>
    <rect x="150" y="68"  width="360" height="40" rx="8" opacity="0.75"/>
    <rect x="150" y="120" width="360" height="40" rx="8" opacity="0.6"/>
    <rect x="150" y="172" width="360" height="40" rx="8" opacity="0.45"/>
    <rect x="150" y="224" width="360" height="40" rx="8" opacity="0.3"/>
  </g>
  <g fill="currentColor" font-size="15" font-weight="bold" text-anchor="middle">
    <text x="330" y="42">① 質問回答書</text>
    <text x="330" y="94">② 現場説明書</text>
    <text x="330" y="146">③ 特記仕様書</text>
    <text x="330" y="198">④ 別冊の図面</text>
    <text x="330" y="250">⑤ 標準仕様書（本書）</text>
  </g>
  <g stroke="currentColor" stroke-width="2" opacity="0.6">
    <line x1="80" y1="30" x2="80" y2="250"/>
  </g>
  <g fill="currentColor" opacity="0.6">
    <polygon points="80,256 74,244 86,244"/>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="30" y="34">優先度</text>
    <text x="42" y="52">高</text>
    <text x="42" y="240">低</text>
    <text x="530" y="42">後から作られ、</text>
    <text x="530" y="60">個別事情を反映</text>
    <text x="530" y="78">するものほど優先</text>
  </g>
</svg>`;

const SVG_COMMUNICATION = `
<svg viewBox="0 0 640 320" role="img" aria-label="受注者と監督職員のやりとり5種類。報告と提出は受注者から監督職員へ、指示は監督職員から受注者へ、承諾は申出への書面の了解、協議は双方向">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="24"  y="40" width="150" height="248" rx="10"/>
    <rect x="466" y="40" width="150" height="248" rx="10"/>
  </g>
  <g fill="currentColor" font-size="15" font-weight="bold" text-anchor="middle">
    <text x="99"  y="28">受注者（あなた）</text>
    <text x="541" y="28">監督職員</text>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="190" y1="80"  x2="440" y2="80"/>
    <line x1="190" y1="130" x2="440" y2="130"/>
    <line x1="450" y1="180" x2="200" y2="180"/>
    <line x1="190" y1="230" x2="440" y2="230"/>
    <line x1="450" y1="252" x2="200" y2="252"/>
    <line x1="190" y1="292" x2="440" y2="292"/>
    <line x1="450" y1="292" x2="200" y2="292"/>
  </g>
  <g fill="currentColor">
    <polygon points="450,80 438,74 438,86"/>
    <polygon points="450,130 438,124 438,136"/>
    <polygon points="190,180 202,174 202,186"/>
    <polygon points="450,230 438,224 438,236"/>
    <polygon points="190,252 202,246 202,258"/>
    <polygon points="450,292 438,286 438,298"/>
    <polygon points="190,292 202,286 202,298"/>
  </g>
  <g fill="currentColor" font-size="13" text-anchor="middle">
    <text x="320" y="70" font-weight="bold">報告：状況・結果を書面で知らせる</text>
    <text x="320" y="120" font-weight="bold">提出：書面・資料を説明して差し出す</text>
    <text x="320" y="170" font-weight="bold">指示：必要な事項を書面で示される</text>
    <text x="320" y="220" font-weight="bold">承諾：書面で申し出て…</text>
    <text x="320" y="272">…書面で了解をもらう</text>
    <text x="320" y="312" font-weight="bold">協議：合議して結論を書面に残す</text>
  </g>
</svg>`;

const SVG_PLAN_DOCS = `
<svg viewBox="0 0 640 250" role="img" aria-label="施工計画書の体系。着工前に総合施工計画書を提出し、各工事の施工前に工種別施工計画書を提出する。品質計画の部分は監督職員の承諾が必要">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30"  y="40" width="260" height="90" rx="10"/>
    <rect x="350" y="40" width="260" height="90" rx="10"/>
    <rect x="120" y="170" width="400" height="52" rx="10" stroke-dasharray="6 4"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="296" y1="85" x2="340" y2="85"/>
  </g>
  <g fill="currentColor">
    <polygon points="350,85 338,79 338,91"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="160" y="66">総合施工計画書</text>
    <text x="480" y="66">工種別施工計画書</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.8">
    <text x="160" y="90">工事全般の総合的な計画</text>
    <text x="160" y="110">着工前に「提出」</text>
    <text x="480" y="90">工種ごとの品質計画・施工計画</text>
    <text x="480" y="110">当該工事の施工前に「提出」</text>
    <text x="320" y="192" font-weight="bold">品質計画に係る部分だけは「承諾」が必要</text>
    <text x="320" y="212">（変更するときも同じく承諾）</text>
  </g>
</svg>`;

const SVG_MATERIAL_FLOW = `
<svg viewBox="0 0 640 240" role="img" aria-label="材料検査の流れ。搬入ごとに報告し、種別ごとに検査。合格なら使用でき、以後は抽出検査にできる。不合格なら直ちに場外搬出">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="40" width="120" height="50" rx="8"/>
    <rect x="180" y="40" width="140" height="50" rx="8"/>
    <rect x="360" y="40" width="120" height="50" rx="8"/>
    <rect x="520" y="16" width="110" height="66" rx="8"/>
    <rect x="520" y="120" width="110" height="66" rx="8"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="140" y1="65" x2="170" y2="65"/>
    <line x1="320" y1="65" x2="350" y2="65"/>
    <line x1="480" y1="55" x2="510" y2="45"/>
    <line x1="480" y1="75" x2="510" y2="145"/>
  </g>
  <g fill="currentColor">
    <polygon points="180,65 168,59 168,71"/>
    <polygon points="360,65 348,59 348,71"/>
    <polygon points="520,42 507,40 511,51"/>
    <polygon points="520,148 507,146 511,136"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="80"  y="61">材料搬入</text>
    <text x="250" y="61">監督職員に報告</text>
    <text x="420" y="61">種別ごとに</text>
    <text x="420" y="78">検査</text>
    <text x="575" y="40">合格 → 使用</text>
    <text x="575" y="152">不合格</text>
  </g>
  <g fill="currentColor" font-size="11" text-anchor="middle" opacity="0.8">
    <text x="80"  y="78">（搬入ごと）</text>
    <text x="575" y="58">同種は以後</text>
    <text x="575" y="72">抽出検査可</text>
    <text x="575" y="168">直ちに</text>
    <text x="575" y="180">場外搬出</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="24" y="220">※ JIS・JASマーク表示品や規格証明書付きの材料は、所定の品質を有するものとして扱える（1.4.4(4)）</text>
  </g>
</svg>`;

const SVG_PROCESS_CHECK = `
<svg viewBox="0 0 640 150" role="img" aria-label="一工程の施工の確認と報告の流れ。一工程の施工が完了したら、自分たちで設計図書への適合を確認し、監督職員に報告し、検査を受けてから次の工程へ進む">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="16"  y="40" width="130" height="60" rx="8"/>
    <rect x="180" y="40" width="150" height="60" rx="8"/>
    <rect x="364" y="40" width="120" height="60" rx="8"/>
    <rect x="518" y="40" width="110" height="60" rx="8"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="146" y1="70" x2="170" y2="70"/>
    <line x1="330" y1="70" x2="354" y2="70"/>
    <line x1="484" y1="70" x2="508" y2="70"/>
  </g>
  <g fill="currentColor">
    <polygon points="180,70 168,64 168,76"/>
    <polygon points="364,70 352,64 352,76"/>
    <polygon points="518,70 506,64 506,76"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="81"  y="66">一工程の施工</text>
    <text x="81"  y="84">完了</text>
    <text x="255" y="66">自主確認</text>
    <text x="424" y="66">監督職員に</text>
    <text x="424" y="84">報告</text>
    <text x="573" y="66">検査を経て</text>
    <text x="573" y="84">次の工程へ</text>
  </g>
  <g fill="currentColor" font-size="11" text-anchor="middle" opacity="0.8">
    <text x="255" y="84">設計図書に適合？</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="16" y="132">※ 確認・報告は、監督職員の承諾を受けた者が行う</text>
  </g>
</svg>`;

const SVG_INSPECTIONS = `
<svg viewBox="0 0 640 230" role="img" aria-label="検査の種類。工事検査は契約書に基づき完成の確認や出来形の確認を行う。技術検査は品確法に基づき施工状況の確認と評価を行い、中間技術検査もある">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30"  y="30" width="270" height="170" rx="10"/>
    <rect x="340" y="30" width="270" height="170" rx="10"/>
  </g>
  <g fill="currentColor" font-size="15" font-weight="bold" text-anchor="middle">
    <text x="165" y="58">工事検査</text>
    <text x="475" y="58">技術検査</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.8">
    <text x="165" y="80">（契約書に基づく）</text>
    <text x="475" y="80">（品確法に基づく）</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="52" y="110">・工事完成の確認</text>
    <text x="52" y="132">・部分払の出来形部分等の確認</text>
    <text x="52" y="154">・部分引渡し部分の完成確認</text>
    <text x="52" y="184" opacity="0.75">→ 合否を判定する検査</text>
    <text x="362" y="110">・施工状況の確認と「評価」</text>
    <text x="362" y="132">・工事検査と同時に実施</text>
    <text x="362" y="154">・中間技術検査は特記による</text>
    <text x="362" y="184" opacity="0.75">→ 工事成績の評定につながる</text>
  </g>
</svg>`;

const ch01: Record<string, Commentary> = {
  "1.1.1": {
    plainSummary:
      "この仕様書は建物の新築・増築工事のルールブック。設計図書どうしに食い違いがあったら「質問回答書→現場説明書→特記仕様書→図面→標準仕様書」の順に優先する。",
    why: "工事では図面・特記仕様書・標準仕様書など複数の書類を同時に使うため、内容の食い違いが必ず起こります。あとから作られ、その工事の個別事情を反映している書類ほど優先度が高い、という考え方です。標準仕様書は「全国共通の標準ルール」なので一番最後になります。",
    points: [
      "図面と特記仕様書が食い違ったら、特記仕様書が優先",
      "順位で判断できない・しづらい場合は、自己判断せず監督職員と協議（1.1.8）",
      "2章以降のどの工事でも、この1章がセットで適用されることを忘れない",
    ],
    glossary: [
      {
        term: "設計図書",
        meaning:
          "図面・標準仕様書・特記仕様書・現場説明書・質問回答書の総称。工事の「正」となる書類一式。",
      },
      {
        term: "特記仕様書",
        meaning:
          "その工事だけの個別ルールを書いた仕様書。標準仕様書より優先される。「特記による」とあれば、まずここを見る。",
      },
      {
        term: "質問回答書",
        meaning:
          "入札前の質疑応答をまとめた書類。設計図書の中で最優先。",
      },
    ],
    diagrams: [
      {
        svg: SVG_PRIORITY,
        caption: "設計図書の優先順位（1.1.1(4)）",
      },
    ],
  },

  "1.1.2": {
    plainSummary:
      "現場で毎日使う「承諾」「指示」「協議」「報告」「提出」などの言葉の意味を決めている条項。どれも原則「書面」で行うのがポイント。",
    why: "「言った・言わない」のトラブルを防ぐためです。たとえば「承諾」は、こちらから書面で申し出て監督職員が書面でOKすること。「指示」は監督職員から書面で示されること。方向と手段（書面）が決まっているので、口頭だけのやりとりでは要件を満たしません。ここの用語は仕様書全体で使われるため、最初に覚えると後の章がぐっと読みやすくなります。",
    points: [
      "口頭でのやりとりだけで工事を進めない。必ず書面（メール・情報共有システム可：1.1.5）に残す",
      "「承諾」（相手のOKが必要）と「報告」（知らせるだけ）の違いを意識する",
      "「特記による」＝標準仕様書以外の設計図書（質問回答書・現場説明書・特記仕様書・図面）に書いてある、という意味",
    ],
    glossary: [
      {
        term: "監督職員",
        meaning: "発注者側で工事を監督する人。契約書に基づく監督員・監督官を含む。",
      },
      {
        term: "受注者等",
        meaning: "工事を請け負った会社、または現場代理人。つまり「あなたの側」。",
      },
      {
        term: "書面",
        meaning: "発行年月日と氏名が記載された文書。日付と名前のないメモは書面にならない。",
      },
      {
        term: "一工程の施工",
        meaning:
          "同一の材料・同一の施工方法で行う作業のまとまりで、監督職員の承諾を受けたもの。検査や報告の単位になる。",
      },
      {
        term: "概成工期",
        meaning:
          "総合試運転調整を行うために、各工事が支障のない状態まで完了しているべき期限。",
      },
    ],
    diagrams: [
      {
        svg: SVG_COMMUNICATION,
        caption: "監督職員とのやりとり5種類（すべて書面が原則）",
      },
    ],
  },

  "1.1.3": {
    plainSummary:
      "着工・施工・完成の各段階で必要な役所への届出を遅れずに行う。届出内容は事前に監督職員へ報告する。",
    why: "建築工事は、消防署・労働基準監督署・道路管理者など多くの機関への届出が必要です。届出が遅れると着工や検査が止まり、工程全体に影響します。「届出→監督職員に事前報告」の順番も決まっています。",
    points: [
      "着工前に「この工事で必要な届出リスト」を作って抜けを防ぐ",
      "届出の前に、内容を監督職員へ報告する",
      "役所の検査に必要な資機材・人員はこちらで用意する",
    ],
  },

  "1.1.4": {
    plainSummary:
      "コリンズ登録が特記された場合は、受注時・変更時・完成時のそれぞれ10日以内（行政休日は除く）に登録する。",
    why: "コリンズは公共工事の実績データベースで、会社の実績や技術者の配置情報が記録されます。登録が遅れると是正対象になるため、期限（10日、休日除く）が明確に決められています。",
    points: [
      "10日のカウントに行政機関の休日は含まない",
      "登録内容は事前に監督職員の確認を受ける",
      "登録後は「登録されたことを証明する資料」を提出する",
    ],
    glossary: [
      {
        term: "コリンズ（CORINS）",
        meaning:
          "工事実績情報システム。公共工事の受注・技術者情報を登録する全国データベース。",
      },
    ],
  },

  "1.1.5": {
    plainSummary:
      "書面の書式は公共建築工事標準書式が基本。承諾・指示・協議・報告・提出は、メールや情報共有システムでも行える（適用は特記による）。",
    why: "従来は紙でのやりとりが原則でしたが、現在は電子メールや情報共有システム（ASPなど）が正式に認められています。ただし、どのシステムを使うかは特記で決まるので、契約時に確認が必要です。",
    points: [
      "書式・提出部数は監督職員と協議して決める",
      "情報共有システムを使うかどうかは特記を確認",
      "施工体制台帳・施工体系図は法律に基づき作成し、写しを監督職員へ提出",
    ],
    glossary: [
      {
        term: "施工体制台帳",
        meaning:
          "元請・下請の契約関係や技術者配置をまとめた台帳。建設業法で作成が義務付けられている。",
      },
    ],
  },

  "1.1.6": {
    plainSummary:
      "設計図書は工事現場に常備する。工事の目的以外で第三者に見せたり、内容を漏らしたりしてはいけない。",
    why: "設計図書には建物のセキュリティに関わる情報（鍵室の位置、設備系統など）が含まれます。漏洩すると発注者に重大な不利益が生じるため、守秘義務が明記されています。",
    points: [
      "現場事務所に最新版の設計図書を備えておく",
      "工事写真やSNS投稿に図面が写り込まないよう注意",
      "第三者に見せる必要がある場合は、事前に監督職員の承諾を受ける",
    ],
  },

  "1.1.7": {
    plainSummary:
      "電気・機械など別発注の「関連工事等」との調整は監督職員が行う。受注者はその調整に協力し、工事全体が円滑に進むよう努める。",
    why: "公共建築では建築・電気・機械が別契約で発注されることが多く、工程やスペースの取り合いが発生します。調整役は監督職員ですが、実際に現場をまとめるのは各受注者の協力です。",
    glossary: [
      {
        term: "関連工事等",
        meaning:
          "契約書に基づく関連工事と、設計図書に明示された他の発注者の工事。同じ建物の電気工事・機械設備工事が典型例。",
      },
    ],
  },

  "1.1.8": {
    plainSummary:
      "設計図書に疑問があるとき、図面どおりでは納まらないときは、自己判断せず監督職員と協議する。協議の結果は記録に残す。",
    why: "現場では「図面どおりに施工できない」「AとBの図面が合わない」ことが日常的に起こります。勝手に判断して施工すると、手戻り・契約トラブル・品質問題につながります。「困ったら協議」はこの仕様書で最も大事な行動原則の一つです。",
    points: [
      "「多分こうだろう」で施工しない。まず協議",
      "協議の結果、設計変更に至らなかった場合でも記録を整備する",
      "訂正・変更になる場合の手続きは契約書の規定による",
    ],
  },

  "1.1.9": {
    plainSummary:
      "埋蔵文化財、関連工事の遅れ、周辺環境問題、安全確保、天災など、受注者の責任でない事由で工事を止める必要が生じたら、直ちに監督職員へ報告する。",
    why: "工事の一時中止は工期や費用に直結するため、発注者側の判断材料として「直ちに」の報告が求められます。報告が遅れると、工期延長や費用の協議で不利になることもあります。",
    points: [
      "報告は「直ちに」。まず一報、詳細は後から",
      "台風・地震などで現場に損害が出た場合も対象",
      "状況は写真等で記録しておくと後の協議で役立つ",
    ],
  },

  "1.1.10": {
    plainSummary:
      "設計変更・一時中止・悪天候などで全体工程に影響が出そうなときは監督職員に報告する。工期変更の協議には、変更日数の算出根拠と変更工程表を事前に提出する。",
    why: "工期を延ばすには客観的な根拠が必要です。「雨が多かった」ではなく「作業不能日が何日あり、どの工程に何日影響したか」を示せて初めて協議になります。日々の記録がそのまま武器になります。",
    points: [
      "悪天候による作業不能日は、日報などで毎日記録しておく",
      "工期変更の協議には「変更日数の算出根拠＋変更工程表」をセットで用意",
    ],
  },

  "1.1.11": {
    plainSummary:
      "施工上の工夫から生まれた材料・工法について特許出願するときは、事前に発注者と協議する。",
    why: "公共工事の中で生まれた発明は、発注者の費用や情報が関わっているため、権利の扱いを事前に整理しておく必要があるからです。",
  },

  "1.1.12": {
    plainSummary:
      "工事中に埋蔵文化財などを発見したら、直ちに監督職員へ報告し、その後は指示に従う。発見物の権利は発注者に帰属する。",
    why: "文化財保護法により、埋蔵文化財の発見時は工事を止めて調査が必要になる場合があります。勝手に掘り進めると法令違反になるおそれがあります。",
    points: [
      "土工事中に土器・瓦・古い基礎などが出たら、まず作業を止めて報告",
      "「これくらい大丈夫」と掘り進めない",
    ],
  },

  "1.1.13": {
    plainSummary:
      "関係法令を守って工事を円滑に進める。当たり前のことだが、仕様書としても明記されている。",
    why: "建築基準法・労働安全衛生法・建設業法など、工事は多くの法律の上に成り立っています。法令違反は仕様書違反でもある、という位置づけです。",
  },

  "1.1.14": {
    plainSummary:
      "遠隔臨場＝カメラとWeb会議システムで映像・音声を配信し、監督職員の立会いを遠隔で行う仕組み。適用するかどうか・内容は特記による。",
    why: "監督職員が現場に来なくても立会い等ができるため、検査待ちの時間を減らせます。使う場合の機材や運用は特記で決まります。",
    glossary: [
      {
        term: "遠隔臨場",
        meaning:
          "動画撮影用カメラ等とWeb会議システム等で映像と音声を配信し、監督職員の立会い等を遠隔で行うこと。",
      },
    ],
  },

  "1.2.1": {
    plainSummary:
      "着工前に実施工程表を作り、監督職員の「承諾」を受ける。条件変更で変えるときも、施工前に承諾を受け直す。",
    why: "実施工程表は工事全体の進め方の約束です。「提出」ではなく「承諾」が必要な点が重要で、監督職員のOKをもらって初めて有効になります。週間・月間工程表は指示があれば補足として提出します。",
    points: [
      "実施工程表は「承諾」（提出しただけでは足りない）",
      "関連工事等の関係者と調整してから作成する",
      "概成工期が特記されたら、受電日・総合試運転調整の工程も明記",
    ],
  },

  "1.2.2": {
    plainSummary:
      "着工前に「総合施工計画書」を、各工事の施工前に「工種別施工計画書」を提出する。品質計画の部分だけは「承諾」が必要。",
    why: "施工計画書は「この工事をどう造るか」の宣言です。全体像（総合）と工種ごとの詳細（工種別）の二段構え。特に品質計画は、その工事の品質の約束事なので、監督職員の承諾が必要とされ、変更時にも再承諾が求められます。",
    points: [
      "総合施工計画書 → 着工前、工種別施工計画書 → その工事の施工前",
      "品質計画に係る部分は承諾。変更時も承諾",
      "工種別は、あらかじめ承諾を受ければ省略できる場合がある",
    ],
    glossary: [
      {
        term: "品質計画",
        meaning:
          "要求品質を満たすための材料・仕上げ程度・性能・精度の目標と、品質管理・体制を具体的に示したもの。",
      },
      {
        term: "品質管理",
        meaning: "品質計画の目標を施工段階で実現するための管理項目・方法。",
      },
    ],
    diagrams: [
      {
        svg: SVG_PLAN_DOCS,
        caption: "施工計画書の体系（1.2.2）",
      },
    ],
  },

  "1.2.3": {
    plainSummary:
      "施工図等は施工前に作成し、監督職員の承諾を受ける。関連工事との納まりを十分調整してから作る。変更時も承諾。",
    why: "施工図は設計図を「実際に造れる図」に落とし込んだもの。設備配管・ダクトとの取り合いを調整しないまま施工すると、手戻りの典型パターンになります。",
    points: [
      "承諾を得てから施工に入る",
      "スリーブ・インサート・下地など設備との取り合いを必ず確認",
    ],
    glossary: [
      {
        term: "施工図等",
        meaning: "施工図・現寸図・工作図・製作図など、施工のための詳細図。",
      },
    ],
  },

  "1.2.4": {
    plainSummary:
      "指示・協議の結果、試験、隠ぺいになる部分の施工などは記録を整備し、求められたら提示・提出する。",
    why: "コンクリートに埋まる配筋や、天井裏の下地は、あとから目で確認できません。隠ぺい部の写真や記録がなければ「やっていない」のと同じ扱いになりかねません。記録は自分と会社を守る証拠です。",
    points: [
      "隠ぺいになる前の工事写真は必須（配筋・防水下地など）",
      "一工程の施工が完了したら記録を残す",
      "試験を行ったら「直ちに」記録を作成",
    ],
    glossary: [
      {
        term: "工事関係図書",
        meaning:
          "実施工程表・施工計画書・施工図等・工事写真など、施工や試験の報告・記録に関する図書の総称。",
      },
    ],
  },

  "1.3.1": {
    plainSummary:
      "施工管理体制を確立し、品質・工程・安全の管理を行う。下請負人にも工事関係図書と監督職員の指示を周知徹底する。",
    why: "実際に手を動かすのは下請の職人さんです。元請だけが指示内容を知っていても品質は確保できません。「周知徹底」まで含めて元請の仕事です。",
    points: [
      "朝礼・KY活動・作業打合せで指示内容を確実に伝える",
      "品質・工程・安全は三点セットで管理する",
    ],
  },

  "1.3.2": {
    plainSummary:
      "施工管理技術者は工事に相応した能力を持つ者とし、指導と品質管理を行う。資格などの証明資料を監督職員に提出する。",
    why: "各章に登場する「施工管理技術者」（例：防水施工管理技術者など）の共通ルールです。誰が管理するのかを資料で示すことで、体制の裏付けになります。",
  },

  "1.3.3": {
    plainSummary:
      "電気保安技術者の配置は特記による。事業用電気工作物は電気主任技術者相当、一般用は電気工事士の資格者。資料を提出して承諾を受ける。",
    why: "仮設電源など工事用の電気設備は感電・火災のリスクがあるため、法令上の有資格者による保安が必要です。",
  },

  "1.3.4": {
    plainSummary:
      "工事用電力設備の保安責任者を決めて監督職員に報告する。責任者は法令に基づき保安業務を行う。",
  },

  "1.3.5": {
    plainSummary:
      "行政機関の休日（土日祝・年末年始）は原則施工しない。休日や夜間に施工したいときは、あらかじめ監督職員の承諾を受ける。",
    why: "公共工事は近隣への配慮や監督体制の関係で、施工日・時間に制約があります。「今週の土曜だけ作業したい」は、事前承諾がなければできません。",
    points: [
      "休日作業は必ず事前に承諾を取る（当日連絡はNG）",
      "夜間施工も同様に事前承諾",
      "その他の施工条件は特記を確認",
    ],
  },

  "1.3.6": {
    plainSummary:
      "品質計画に基づいて、適切な時期に品質管理を行う。必要に応じて監督職員の検査を受け、疑義があれば協議する。",
    why: "品質管理は「終わってから確認」では手遅れです。工程の節目ごとに、計画で決めたチェックを実行することが求められます。",
  },

  "1.3.7": {
    plainSummary:
      "法令と安全指針に基づき、災害・事故防止に努める。火気・既設物・気象・近隣対応の基本ルールをまとめた条項。",
    why: "安全は工事の大前提です。特に (4)既設配管等への配慮、(5)火気使用時の火災防止、(6)近隣折衝の記録・報告は、新人がまず現場で直面する場面です。",
    points: [
      "溶接・グラインダー使用時は消火器・防炎シートを必ず準備",
      "掘削前に地下埋設物（ガス・水道・電気）を調査する",
      "近隣から苦情があったら誠意をもって直ちに対応し、経過を記録して監督職員に報告",
      "気象警報が出そうなときは先回りして養生・固定",
    ],
  },

  "1.3.8": {
    plainSummary:
      "資材や土砂の搬送計画・通行経路を関係機関と調整し、交通安全を確保する。",
    points: [
      "通学路・通学時間帯を避けた搬入計画にする",
      "必要に応じて交通誘導員を配置",
    ],
  },

  "1.3.9": {
    plainSummary:
      "災害・事故が起きたら、人命の安全確保を最優先。二次災害を防ぎ、直ちに監督職員へ報告する。",
    why: "順番が大事です。①人命（救護・退避）→②二次災害防止（現場の安全確保）→③報告。物や工程より人命が常に先です。",
  },

  "1.3.10": {
    plainSummary:
      "騒音・振動・粉じん・水質汚濁などの法令を守り、周辺環境の保全に努める。塗料・接着剤などの化学品はSDSを常備し、内容を作業者に周知する。",
    why: "環境関係の法律は数が多く、違反すると工事停止もあり得ます。また化学品による健康被害（有機溶剤中毒など）を防ぐため、SDS（安全データシート）の常備・表示が義務付けられています。",
    points: [
      "塗料・シーリング材・接着剤を使うときはSDSを現場に常備",
      "取り扱う化学品の情報をラベル等で作業場内に表示",
      "騒音・振動の出る作業は時間帯に配慮",
    ],
    glossary: [
      {
        term: "SDS（安全データシート）",
        meaning:
          "化学品の危険有害性や取扱い方法を記載したシート。製造者が作成し、使用現場に常備する。",
      },
    ],
  },

  "1.3.11": {
    plainSummary:
      "発生材（工事で出た残材・撤去材）は、まず抑制・再利用・再資源化。発注者に引き渡すもの・現場で再資源化するものは特記により、それ以外は法令に基づき適正処理して報告する。",
    why: "建設業は産業廃棄物の主要な排出源です。建設リサイクル法などにより、分別・再資源化・適正処理の流れが厳格に決められており、処理を誤ると排出事業者（元請）の責任が問われます。",
    points: [
      "発注者に引き渡すもの（有価物など）は特記を確認し、調書を作成・提出",
      "再資源化するものは分別して所定の施設へ搬入",
      "マニフェスト（産業廃棄物管理票）の運用を徹底",
    ],
    glossary: [
      {
        term: "発生材",
        meaning: "工事に伴って発生する残材・撤去材・梱包材など。",
      },
      {
        term: "マニフェスト",
        meaning:
          "産業廃棄物管理票。廃棄物が最終処分まで適正に処理されたことを追跡する伝票。",
      },
    ],
  },

  "1.3.12": {
    plainSummary:
      "既存部分や施工済みの部分を汚したり傷つけたりしないよう、適切に養生する。",
    points: [
      "仕上がった床・建具・サッシは施工済み＝守るべき対象と意識する",
      "改修工事では既存部分の養生範囲を事前に確認",
    ],
  },

  "1.3.13": {
    plainSummary: "工事の完成にあたり、建物内外の後片付けと清掃を行う。",
    why: "引渡し前の最後の印象を決める作業です。検査前の清掃状態は工事全体の評価にも影響します。",
  },

  "1.4.1": {
    plainSummary:
      "グリーン購入法に基づき環境負荷の少ない材料を選ぶ。VOC（揮発性有機化合物）の健康影響に配慮し、石綿（アスベスト）を含む材料は使わない。",
    glossary: [
      {
        term: "グリーン購入法",
        meaning:
          "国等が環境物品（エコマーク製品など）を優先的に調達することを定めた法律。",
      },
      {
        term: "石綿（アスベスト）",
        meaning:
          "かつて建材に多用された繊維状鉱物。吸入すると重い健康被害を起こすため、現在は使用禁止。",
      },
    ],
  },

  "1.4.2": {
    plainSummary:
      "使う材料は設計図書の品質・性能を満たす「新品」。品質の証明資料を提出する。ただしJIS・JASマーク表示品は証明を省略できる。色や柄は見本で承諾を受ける。",
    why: "材料の品質は建物の品質の土台です。全材料の試験は現実的でないため、JIS・JASマークという国の規格制度を信頼して証明を省略できる仕組みになっています。なお「新品」とは製造所出荷状態の品質・性能を持つものを指し、製造直後であることまでは求められていません。",
    points: [
      "JIS・JASマーク表示のある材料は品質証明資料を省略できる",
      "調合が必要な材料（モルタル等）は調合表を提出",
      "色合・柄は見本を提示して監督職員の承諾を受けてから発注",
      "木材関係はグリーン購入法に基づく合法性証明書等を提出",
    ],
    glossary: [
      {
        term: "JIS",
        meaning: "日本産業規格。産業標準化法に基づく国家規格。",
      },
      {
        term: "JAS",
        meaning: "日本農林規格。木材・合板などの品質規格。",
      },
      {
        term: "規格証明書",
        meaning: "規格・基準に適合することを証明する、制度上定められた者が発行する資料。",
      },
    ],
  },

  "1.4.3": {
    plainSummary:
      "材料を現場に搬入するたびに監督職員へ報告する（あらかじめ承諾を受けた場合は省略可）。",
    why: "何がいつ現場に入ったかを発注者側も把握することで、材料検査（1.4.4）や数量管理の前提が整います。",
  },

  "1.4.4": {
    plainSummary:
      "搬入した材料は種別ごとに監督職員の検査を受ける。合格した種別は以後抽出検査にできる。不合格品は直ちに場外へ搬出する。",
    why: "不合格材料が現場に残っていると、誤って使用される事故が起こります。「直ちに搬出」は誤使用防止のルールです。JIS・JASマーク品や規格証明書付きの材料は、所定の品質を有するものとして扱えます。",
    points: [
      "検査前に自分で規格・数量・外観をチェックしておく",
      "不合格品は現場に置かず、直ちに搬出",
      "同じ種別の2回目以降は抽出検査にできる（指示があれば全数に戻る）",
    ],
    diagrams: [
      {
        svg: SVG_MATERIAL_FLOW,
        caption: "材料搬入から検査までの流れ（1.4.3〜1.4.4）",
      },
    ],
  },

  "1.4.5": {
    plainSummary:
      "材料の品質を試験で証明する場合は、設計図書の試験方法により、試験計画書の承諾→立会い→結果報告の順で進める。",
    points: [
      "試験の前に試験計画書を作成し承諾を受ける",
      "試験は原則監督職員の立会い付き",
      "結果は必ず報告する",
    ],
  },

  "1.4.6": {
    plainSummary:
      "搬入した材料は、使うまで破損・変質しないよう保管する。使えないと指示された材料は直ちに場外へ搬出する。",
    points: [
      "鉄筋・鉄骨は台木の上に置き、シートで養生（直置き・雨ざらしNG）",
      "セメント・ボード類は湿気を避けて保管",
      "保管方法は各章にも規定があるので併せて確認",
    ],
  },

  "1.5.1": {
    plainSummary:
      "施工は設計図書・実施工程表・施工計画書・施工図等に基づいて行う。設備配管などが隠ぺいになる部分は、設備側の検査が終わるまでこちらの施工を進めない。",
    why: "コンクリートを打ってしまえば中の配管はやり直せません。「関連工事の検査完了を待ってから隠ぺいする」は、建築と設備の連携の基本動作です。",
    points: [
      "コンクリート打込み前に、設備（電気・機械）の検査完了を確認する",
      "承諾を受けた場合のみ例外",
    ],
  },

  "1.5.2": {
    plainSummary:
      "技能士とは一級技能士または単一等級の資格者のこと。適用職種は特記による。技能士は1名以上が自ら作業しつつ、他の作業者への指導も行う。",
    glossary: [
      {
        term: "技能士",
        meaning:
          "職業能力開発促進法に基づく技能検定の合格者。ここでは一級または単一等級を指す。",
      },
    ],
  },

  "1.5.3": {
    plainSummary:
      "技能資格者は工事に相応した能力を持つ者とし、証明資料を監督職員に提出する。",
  },

  "1.5.4": {
    plainSummary:
      "一工程の施工が終わったら、設計図書に適合していることを自分たちで確認し、監督職員に報告する。確認・報告は承諾を受けた者が行う。",
    why: "「造る人が自らチェックし、その結果を発注者が確認する」のが公共工事の品質管理の基本構造です。この自主確認→報告が、次の検査（1.5.5）の入口になります。",
    diagrams: [
      {
        svg: SVG_PROCESS_CHECK,
        caption: "一工程の施工の確認・報告の流れ（1.5.4〜1.5.5）",
      },
    ],
  },

  "1.5.5": {
    plainSummary:
      "設計図書で定められた場合や1.5.4の報告をした場合は、監督職員の検査を受ける。合格した工程と同じ材料・工法の部分は以後抽出検査にできる。検査用の巻尺はJIS 1級。",
    points: [
      "検査の前にセルフチェックを済ませておく（指摘ゼロを目指す）",
      "見本施工が特記されていたら、仕上がりが判断できる見本を作って承諾を受ける",
      "基準巻尺はJIS B 7512の1級を使う",
    ],
  },

  "1.5.6": {
    plainSummary:
      "施工の検査に伴う試験は、材料試験のルール（1.4.5）に準じて行う。",
  },

  "1.5.7": {
    plainSummary:
      "関連工事と合同で行う総合試運転調整では、防火設備等を関連機器と連動させ、設計どおりの機能を確認する。その他は協議。",
    glossary: [
      {
        term: "総合試運転調整",
        meaning:
          "建物の使用を想定し、建築・電気・機械の設備を連動させて機能を確認する調整作業。",
      },
    ],
  },

  "1.5.8": {
    plainSummary:
      "設計図書で定められた場合や指示された場合の施工は、監督職員の立会いを受ける。立会いに必要な資機材・人員はこちらで用意する。",
  },

  "1.5.9": {
    plainSummary:
      "設計図書と違う工法でも、品質を確保できる・環境に良い・生産性が上がる提案があれば、監督職員と協議できる。",
    why: "現場の創意工夫を活かす窓口です。ただし勝手に変えるのではなく、必ず「協議」を通すのが条件です。",
  },

  "1.5.10": {
    plainSummary:
      "室内空気中の化学物質（ホルムアルデヒド等）の濃度測定は特記による。測定したら結果を監督職員に提出する。",
    why: "シックハウス対策です。内装材や接着剤から放散される化学物質が基準以下であることを、引渡し前に確認します。",
  },

  "1.6.1": {
    plainSummary:
      "「工事完成しました」の通知は、①指示事項がすべて完了、②工事関係図書の整備がすべて完了、の両方を満たして初めて出せる。検査は通知後、発注者が指定した検査日に受ける。",
    why: "完成検査は工事の集大成です。書類が揃っていない状態で通知を出すことはできません。日頃から工事関係図書を整備しておくことが、スムーズな検査への近道です。",
    points: [
      "完成通知の前に、指示事項の完了と図書整備を自己点検する",
      "部分払を請求するときは、出来形部分の算出方法について指示を受ける",
      "検査に必要な資機材・人員はこちらで用意",
    ],
    glossary: [
      {
        term: "出来形",
        meaning: "工事の進捗に応じて実際に完成している部分のこと。部分払の根拠になる。",
      },
    ],
    diagrams: [
      {
        svg: SVG_INSPECTIONS,
        caption: "工事検査と技術検査の違い（1.6.1〜1.6.2）",
      },
    ],
  },

  "1.6.2": {
    plainSummary:
      "技術検査は品確法に基づき、施工状況を確認・評価する検査。工事検査のとき＋中間技術検査（特記）＋発注者が必要と認めたときに行われる。",
    why: "工事検査が「合否」を見るのに対し、技術検査は「評価」も行い、工事成績評定につながります。成績は会社の次の受注にも影響する大事な検査です。",
  },

  "1.7.1": {
    plainSummary:
      "完成時に提出する図書は特記による。特記がなければ、完成図（1.7.2）と保全に関する資料（1.7.3）を提出する。",
  },

  "1.7.2": {
    plainSummary:
      "完成図は工事目的物の完成時の状態を表した図面。特記がなければ、配置図・案内図、各階平面図、立面図、断面図、仕上表を作成する。",
    points: [
      "工事中の設計変更を反映した「最終の姿」を描く",
      "記入内容は表1.7.1（室名・面積・耐震壁・仕上げなど）を確認",
    ],
  },

  "1.7.3": {
    plainSummary:
      "保全に関する資料＝建物の「取扱説明書」一式。利用説明書、機器取扱説明書、機器性能試験成績書、官公署届出書類、総合試運転報告書を提出する。",
    why: "建物は引渡し後、施設管理者が何十年も維持管理します。そのための資料を揃えるのも工事の一部です。記載事項は監督職員と協議して決めます。",
  },
};

export default ch01;
